import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import WizardProgress from '../site-setup-wizard/components/WizardProgress';
import { setupService } from '../../services/setupService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';
import { sanitizeError } from '../../utils/sanitizeError';

const SetupFlowChecklistConfirmation = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [siteId, setSiteId] = useState(null);
  const [checklistName, setChecklistName] = useState('Daily Site Checks');
  const [error, setError] = useState('');

  const steps = [
    { id: 1, title: 'Company Details' },
    { id: 2, title: 'Choose Plan' },
    { id: 3, title: 'Create First Site' },
    { id: 4, title: 'Invite Team' },
    { id: 5, title: 'Daily Site Checklist' }
  ];

  const defaultChecklistItems = [
    { text: 'Site access and security check', required: true },
    { text: 'PPE compliance verification', required: true },
    { text: 'Equipment inspection', required: true },
    { text: 'Hazard identification', required: true },
    { text: 'Welfare facilities check', required: true }
  ];

  useEffect(() => {
    loadSetupData();
  }, []);

  const loadSetupData = async () => {
    try {
      const { data, error } = await companyService?.getUserCompany();
      if (error) throw error;
      
      if (data) {
        setCompanyId(data?.id);
      }

      const savedProgress = setupService?.loadProgress();
      if (savedProgress?.formData?.siteId) {
        setSiteId(savedProgress?.formData?.siteId);
      }
      if (savedProgress?.formData?.checklistName) {
        setChecklistName(savedProgress?.formData?.checklistName);
      }
    } catch (err) {
      console.error('Failed to load setup data:', err);
      setError('Failed to load setup data');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/setup-flow-invite-team');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!checklistName?.trim()) {
      setError('Checklist name is required');
      return;
    }

    if (!siteId) {
      setError('Site not found. Please go back and create a site first.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error: checklistError } = await setupService?.findOrUpdateDefaultChecklist(siteId, checklistName);
      if (checklistError) throw checklistError;

      // setup_complete and onboarding_finished are set by stripe-webhook on
      // checkout.session.completed, NOT here. Writing them here would let users
      // bypass the paywall by completing the setup flow without paying.
      setupService?.clearProgress();
      navigate('/admin-dashboard');
    } catch (err) {
      setError(sanitizeError(err?.message, 'Failed to complete setup'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Daily Site Checklist</h1>
          <p className="text-muted-foreground">Review and confirm your daily site checks template</p>
        </div>

        <WizardProgress currentStep={5} totalSteps={5} steps={steps} onExit={() => navigate('/')} />

        <div className="bg-card rounded-lg shadow-elevation-1 p-6 md:p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Daily Site Checks</h2>
            
            <div className="bg-success/10 border border-success/20 rounded-md p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-success mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Almost Done!</p>
                  <p className="text-sm text-muted-foreground">
                    We've prepared a default daily site checklist. You can customise it from the Checklist Management page after setup.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Checklist Name"
              name="checklistName"
              value={checklistName}
              onChange={(e) => { setChecklistName(e?.target?.value); setError(''); }}
              error={!checklistName?.trim() ? error : ''}
              required
              placeholder="e.g., Daily Site Checks"
              description="You can rename this checklist later"
            />

            <div className="bg-muted/50 rounded-md p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Default Checklist Items</h3>
              <div className="space-y-2">
                {defaultChecklistItems?.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Icon name="Check" size={16} className="text-accent mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-foreground">{item?.text}</p>
                      {item?.required && (
                        <span className="text-xs text-muted-foreground">Required</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                You can add, edit, or remove checklist items from the Daily Site Checklist page after setup.
              </p>
            </div>

            {error && checklistName?.trim() && (
              <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="flex justify-between items-center pt-4">
              <Button type="button" variant="outline" onClick={handleBack} disabled={isSubmitting}>
                Back
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting || !checklistName?.trim()}
                size="lg"
                variant="success"
              >
                Complete Setup & Start Using SiteProof
              </Button>
            </div>
          </form>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Step 5 of 5 — Final step!
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetupFlowChecklistConfirmation;